import React, { useEffect, useRef, useState } from 'react';
import { canvasAJpeg, medidasReducidas, reducirFoto } from '../../lib/permisos-evidencia';

// "Tomar foto" en el momento:
//  - celular o tablet (puntero táctil): <input type="file" capture> abre la cámara;
//  - laptop o PC: getUserMedia muestra la cámara y "Capturar" toma el cuadro;
//  - sin cámara o sin permiso: se avisa y queda "Subir imagen".
// Todas las fotos salen en JPEG de 1600 px como máximo (reducirFoto).
export type OrigenFoto = 'camara' | 'archivo';

interface Props {
  disabled?: boolean;
  onFoto: (foto: Blob, origen: OrigenFoto) => void | Promise<void>;
}

function esTactil(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
}

export default function PhotoCapture({ disabled, onFoto }: Props) {
  const camaraInput  = useRef<HTMLInputElement>(null);
  const archivoInput = useRef<HTMLInputElement>(null);
  const videoRef     = useRef<HTMLVideoElement>(null);
  const streamRef    = useRef<MediaStream | null>(null);
  const [abierta,   setAbierta]   = useState(false);
  const [aviso,     setAviso]     = useState<string | null>(null);
  const [procesando, setProcesando] = useState(false);

  function detenerCamara() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setAbierta(false);
  }

  useEffect(() => () => { streamRef.current?.getTracks().forEach(t => t.stop()); }, []);

  async function tomarFoto() {
    setAviso(null);
    if (esTactil() || !navigator.mediaDevices?.getUserMedia) {
      camaraInput.current?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      streamRef.current = stream;
      setAbierta(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch {
      setAviso('No se encontró una cámara o no se dio permiso. Puede subir una imagen.');
    }
  }

  async function capturar() {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    setProcesando(true);
    try {
      const { ancho, alto } = medidasReducidas(video.videoWidth, video.videoHeight, 1600);
      const foto = await canvasAJpeg(ancho, alto, ctx => ctx.drawImage(video, 0, 0, ancho, alto));
      detenerCamara();
      await onFoto(foto, 'camara');
    } catch (e) {
      setAviso((e as Error).message);
    } finally {
      setProcesando(false);
    }
  }

  async function desdeInput(e: React.ChangeEvent<HTMLInputElement>, origen: OrigenFoto) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setAviso('El archivo no es una imagen.'); return; }
    setProcesando(true);
    try {
      await onFoto(await reducirFoto(file), origen);
    } catch (err) {
      setAviso((err as Error).message);
    } finally {
      setProcesando(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={tomarFoto} disabled={disabled || procesando || abierta} style={btn}>
          📷 Tomar foto
        </button>
        <button type="button" onClick={() => archivoInput.current?.click()} disabled={disabled || procesando} style={btn}>
          Subir imagen
        </button>
        {procesando && <span style={{ color: '#64748b', fontSize: '0.76rem', alignSelf: 'center' }}>Procesando…</span>}
      </div>
      <input ref={camaraInput} type="file" accept="image/*" capture="environment" hidden onChange={e => desdeInput(e, 'camara')} />
      <input ref={archivoInput} type="file" accept="image/jpeg,image/png" hidden onChange={e => desdeInput(e, 'archivo')} />
      {aviso && <span style={{ color: '#fbbf24', fontSize: '0.78rem' }}>{aviso}</span>}
      {abierta && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 10 }}>
          <video ref={videoRef} playsInline muted style={{ width: '100%', maxHeight: 360, borderRadius: 8, background: '#000' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={detenerCamara} style={btn}>Cancelar</button>
            <button type="button" onClick={capturar} disabled={procesando} style={{ ...btn, borderColor: '#2FBF71', color: '#2FBF71' }}>Capturar</button>
          </div>
        </div>
      )}
    </div>
  );
}

const btn: React.CSSProperties = { padding: '7px 14px', background: 'transparent', border: '1px solid #334155', color: '#cbd5e1', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 };
